import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, CircularProgress, FormControl, Grid, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useSelector } from 'react-redux';
import { RootState } from '../../../app/store';
import configUrls from '../../../config/configUrls';
import { DocumentoEmailList } from '../../../types/documento/email/DocumentoEmailList';
import VisibilityIcon from '@mui/icons-material/Visibility';

import ConfirmDialogList from '../../../components/ConfirmDialogList';
import { DocumentoEmail, ISdocumentoEmail } from '../../../types/documento/email/DocumentoEmail';
import { useDeleteEmailDocumentoMutation, useGetDocumentosEmailsQuery } from './DocumentoEmailApiSlice';

export const DocumentoEmailListForm = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate(); // Hook para navegação

  const { processoSelecionado } = useSelector((state: RootState) => state.processoSelecionado);
  useEffect(() => {
    if (!processoSelecionado) {
      alert('Nenhum documento selecionado');
      window.location.href = configUrls.PROCESSO_LIST_RT;
    }
  }, [processoSelecionado]);

  /* DIALOG EXCLUIR */
  const [deleteDocumentoEmail, { error: errorDelete, isLoading, isSuccess }] = useDeleteEmailDocumentoMutation();
  const [openConfirmExcluir, setOpenConfirmExcluir] = useState(false);
  const [emailToDelete, setDocumentoEmailToDelete] = useState<DocumentoEmail>(ISdocumentoEmail);

  const handleCloseDialog = () => {
    setOpenConfirmExcluir(false);
  };

  const handleExcluirtClick = (rowData: any) => {
    setDocumentoEmailToDelete(rowData);
    setOpenConfirmExcluir(true);
  };

  const handleConfirm = async () => {
    setOpenConfirmExcluir(false);
    await deleteDocumentoEmail(emailToDelete.id ? emailToDelete.id : '');
  };
  useEffect(() => {
    if (isSuccess) {
      enqueueSnackbar('Registro deletado com sucesso!', { variant: 'success' });
      setDebouncedSearch(search);
    }
    if (errorDelete) {
      enqueueSnackbar('Registro não deletado!', { variant: 'error' });
    }
  }, [isSuccess, errorDelete]);

  /*SEARCH*/

  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cursorPosition = useRef<number>(0);

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    cursorPosition.current = inputRef.current?.selectionStart || 0;
    setSearch(e.target.value);
  };

  useEffect(() => {
    // Limpar o timeout anterior
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Configurar o timeout para o debounce
    debounceTimeout.current = setTimeout(() => {
      
      setDebouncedSearch(search);
    }, 700); // Tempo de debounce (700ms)

    // Limpar o timeout quando o componente for desmontado ou o efeito for reiniciado
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [search]);

  // FIM DO SEARCH

  const [emails, setEmails] = useState<DocumentoEmailList[]>([]);
  const idEnvio = useParams().id || '';

  const { data, isFetching, error } = useGetDocumentosEmailsQuery({ id: idEnvio ?? '', search: debouncedSearch });
  useEffect(() => {
    if (data) {
      setEmails(data);
    }
    if (error) {
      enqueueSnackbar('Emails não encontradas!', { variant: 'error' });
    } else if (isFetching) {
      enqueueSnackbar('Buscando registros...', { variant: 'info' });
    }

    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current?.setSelectionRange(cursorPosition.current, cursorPosition.current);
    }
  }, [data, error, isFetching, enqueueSnackbar]);

  if (isFetching) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }
  const headerGerados = (
    <Typography variant="body2" color="textSecondary">
      Email(s) enviado(s): {emails.length}
    </Typography>
  );

  const renderData = (rowData: DocumentoEmailList) => {
    const dataCriacao = rowData.dataHora ? format(rowData.dataHora, 'dd/MM/yyyy HH:mm:ss') : 'Data não disponível';
    return (
      <Typography color="secondary" variant="body2">
        {dataCriacao}
      </Typography>
    );
  };
  const renderUsuario = (rowData: DocumentoEmailList) => (
    <Typography color="secondary" variant="body2">
      {rowData.usuario?.nome || 'Usuário não disponível'}
    </Typography>
  );

  const renderPara = (rowData: DocumentoEmailList) => (
    <Typography color="secondary" variant="body2">
      {rowData.para || 'Destinatário não disponível'}
    </Typography>
  );

  const renderAssunto = (rowData: DocumentoEmailList) => (
    <Typography color="secondary" variant="body2">
      {rowData.assunto || 'Assunto não disponível'}
    </Typography>
  );

  const renderAction = (rowData: any) => {
    return (
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <Tooltip title="Visualizar">
          <Link to={`${configUrls.DOCUMENTO_EMAIL_CREATE_LINK}${idEnvio}?idEmail=${rowData.id}`}>
            <IconButton color="secondary" aria-label="view">
              <VisibilityIcon />
            </IconButton>
          </Link>
        </Tooltip>

        <Tooltip title="Excluir">
          <IconButton color="secondary" aria-label="delete" onClick={() => handleExcluirtClick(rowData)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  return (
    <>
      <ConfirmDialogList
        open={openConfirmExcluir}
        handleClose={handleCloseDialog}
        title="Confirmar Ação"
        text={['Você tem certeza de que deseja excluir esse regsistro de Envio?', emailToDelete.para ? emailToDelete.para : '']}
        handleDelete={() => {
          handleConfirm(); // Chama a função handleConfirm
          setOpenConfirmExcluir(false); // Fecha o diálogo
        }}
      />
      <Box sx={{ padding: 2 }}>
      <Paper sx={{ width: '100%', height: '100vh', padding: '12px', display: 'flex', flexDirection: 'column' }}>
        
          <Grid container padding={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Box display="flex">
                    <Typography color="secondary" variant="h6" gutterBottom>
                      Emails para o documento: {processoSelecionado?.numeroProcesso}
                      iniciar o compentario do documento https://www.youtube.com/watch?v=H2YzzAPD9yY
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        navigate(`${configUrls.DOCUMENTO_EMAIL_CREATE_LINK}${idEnvio}`);
                      }}
                    >
                      Novo Envio
                    </Button>

                    <Button variant="contained" color="secondary" onClick={() => navigate(-1)}>
                      Voltar
                    </Button>
                  </Box>
                </Box>
              </FormControl>
            </Grid>

            <Grid item xs={12} mt={5}>
              <FormControl fullWidth>
                <TextField label="Digite para pesquisar" name="nome" onChange={handleChangeSearch} size="small" inputProps={{ maxLength: 100 }} value={search} inputRef={inputRef} />
              </FormControl>
            </Grid>
          </Grid>

          <DataTable value={emails} paginator rows={25} showGridlines={true} size="small" columnResizeMode="expand" resizableColumns dataKey="id" header={headerGerados} className="custom-datatable">
            <Column header="Data" body={renderData} sortable style={{ width: '30%' }} />
            <Column header="Destinatário" body={renderPara} sortable style={{ width: '40%' }} />
            <Column header="Assunto" body={renderAssunto} sortable style={{ width: '40%' }} />
            <Column header="Usuário" body={renderUsuario} sortable style={{ width: '30%' }} />
            <Column field="acoes" header="Ações" body={renderAction} style={{ width: 'auto' }}></Column>
          </DataTable>
        </Paper>
      </Box>
    </>
  );
};
